// ============================================================
// NOTIFICHE LOCALI - adapter nativo (Capacitor) + Web fallback
// ============================================================
// FIX IMPORTANTI:
// 1. Crea un NotificationChannel Android esplicito con IMPORTANCE_HIGH
// 2. Schedula con allowWhileIdle: true (funziona anche in doze mode)
// 3. Per i reminder ricorrenti usa schedule.on ogni settimana (vero recurring)
// 4. Richiede esplicitamente il permesso prima di schedulare
// 5. Small icon BIANCO su trasparente (obbligatorio per Android 5+)
// ============================================================

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { Reminder } from '@/types';

const CHANNEL_ID = 'pira-notes-reminders';

let _permissionGranted: boolean | null = null;
let _channelCreated = false;

/** Converte giorno settimana ISO (0=dom, 1=lun, ..., 6=sab) in giorno Capacitor (1=dom, 2=lun, ..., 7=sab). */
function toCapWeekday(day: number): number {
  return day === 0 ? 1 : day + 1;
}

/** Hasha una stringa in un intero positivo per ID notifica. */
function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h) % 2000000000;
}

/** Verifica/crea il notification channel Android (importance HIGH = popup + suono + vibrazione). */
async function ensureChannel(): Promise<void> {
  if (!Capacitor.isNativePlatform() || _channelCreated) return;
  try {
    // Crea il canale con le opzioni avanzate (richiede API >= 26 ma il plugin gestisce il fallback)
    const channel = {
      id: CHANNEL_ID,
      name: 'Promemoria Pira Notes',
      description: 'Notifiche per i promemoria programmati',
      importance: 5, // IMPORTANCE_HIGH - mostra come heads-up
      visibility: 1, // PUBLIC
      sound: undefined, // default
      vibration: true,
      lights: true,
      lightColor: '#6366F1',
    };
    try {
      // Le API per canali sono disponibili su Android 8+
      if (typeof (LocalNotifications as any).createChannel === 'function') {
        await (LocalNotifications as any).createChannel(channel);
      } else if (typeof (LocalNotifications as any).createNotificationChannel === 'function') {
        await (LocalNotifications as any).createNotificationChannel(channel);
      }
    } catch (e) {
      console.warn('[notifications] createChannel non supportato, uso default', e);
    }
    _channelCreated = true;
  } catch (e) {
    console.warn('[notifications] ensureChannel error', e);
  }
}

/**
 * Richiede il permesso notifiche (Android 13+, iOS).
 * Su Android < 13 il permesso è implicito.
 */
export async function requestPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const cur = await LocalNotifications.checkPermissions();
      if (cur.display === 'granted') {
        _permissionGranted = true;
        return true;
      }
      const req = await LocalNotifications.requestPermissions();
      _permissionGranted = req.display === 'granted';
      return _permissionGranted;
    } catch (e) {
      console.warn('[notifications] requestPermission error', e);
      return false;
    }
  }
  if (typeof Notification !== 'undefined') {
    if (Notification.permission === 'granted') {
      _permissionGranted = true;
      return true;
    }
    if (Notification.permission !== 'denied') {
      const p = await Notification.requestPermission();
      _permissionGranted = p === 'granted';
      return _permissionGranted;
    }
  }
  return false;
}

/** Verifica se le notifiche sono permesse. */
export async function checkPermission(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const p = await LocalNotifications.checkPermissions();
      return p.display === 'granted';
    } catch {
      return false;
    }
  }
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/** Elenco delle notifiche pendenti (utile per debug). */
export async function getPendingNotifications(): Promise<any[]> {
  if (Capacitor.isNativePlatform()) {
    try {
      const res = await LocalNotifications.getPending();
      return res.notifications ?? [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Schedula una notifica locale per un reminder.
 * - Se ha `time` + `days_of_week`: NOTIFICA RICORRENTE SETTIMANALE VERA
 *   (su Android usa schedule.on con weekday ricorrente)
 * - Se ha `at` (ISO date): notifica one-shot
 */
export async function scheduleReminder(r: Reminder): Promise<void> {
  if (!r.enabled) return;

  // Assicurati che il permesso ci sia
  if (_permissionGranted === null) {
    await requestPermission();
  }
  if (_permissionGranted === false) {
    console.warn('[notifications] permesso non concesso, skip');
    return;
  }

  await ensureChannel();

  const id = hashId(r.id);
  const body = r.body || 'Promemoria';
  const baseNotif = {
    id,
    title: r.title,
    body,
    smallIcon: 'ic_stat_icon',
    iconColor: '#6366F1',
    channelId: CHANNEL_ID,
    sound: undefined,
    extra: { reminderId: r.id },
  };

  // -------------------------------------------------------
  // ONE-SHOT: data `at` specifica
  // -------------------------------------------------------
  if (r.at && !r.time) {
    const fireAt = new Date(r.at);
    if (fireAt.getTime() <= Date.now()) {
      console.log('[notifications] one-shot già passato, skip');
      return;
    }
    if (Capacitor.isNativePlatform()) {
      try {
        await LocalNotifications.schedule({
          notifications: [
            {
              ...baseNotif,
              schedule: {
                at: fireAt,
                allowWhileIdle: true,
                repeats: false,
              },
            },
          ],
        });
        console.log(`[notifications] one-shot scheduled at ${fireAt.toISOString()} id=${id}`);
      } catch (e) {
        console.error('[notifications] schedule one-shot error', e);
      }
    } else {
      scheduleWebTimeout(r.id, fireAt.getTime() - Date.now(), r.title, body);
    }
    return;
  }

  // -------------------------------------------------------
  // RICORRENTE: time + days_of_week
  // -------------------------------------------------------
  if (r.time) {
    const [h, m] = r.time.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) {
      console.warn('[notifications] time non valido', r.time);
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // Strategia: una notifica per ogni giorno della settimana richiesto
      // (LocalNotifications non supporta nativamente "ogni lun-ven alle 9:00" come singola entry)
      const days = r.days_of_week.length === 0
        ? [0, 1, 2, 3, 4, 5, 6] // tutti i giorni
        : r.days_of_week;

      // Cancella eventuali precedenti notifiche per questo reminder
      await cancelReminder(r.id);

      const notifications: any[] = [];
      for (const day of days) {
        // ID univoco per ogni (reminder, giorno)
        const dayId = hashId(`${r.id}-${day}`);
        const capDay = toCapWeekday(day);
        notifications.push({
          ...baseNotif,
          id: dayId,
          schedule: {
            on: {
              weekday: capDay,
              hour: h,
              minute: m,
            },
            repeats: true,
            every: 'week',
            allowWhileIdle: true,
          },
        });
      }

      try {
        await LocalNotifications.schedule({ notifications });
        console.log(`[notifications] recurring scheduled for ${notifications.length} days at ${r.time} id=${id}`);
      } catch (e) {
        console.error('[notifications] schedule recurring error', e);
      }
    } else {
      // Web: prima occorrenza + ri-schedulazione dopo il fire
      const next = nextOccurrence(r.time, r.days_of_week);
      if (next) {
        const delay = next.getTime() - Date.now();
        scheduleWebTimeout(r.id, delay, r.title, body, () => {
          scheduleReminder(r);
        });
      }
    }
  }
}

/** Cancella una notifica schedulata (tutti i giorni). */
export async function cancelReminder(reminderId: string): Promise<void> {
  cancelWebTimeout(reminderId);
  if (Capacitor.isNativePlatform()) {
    try {
      // Cancella l'ID principale + tutti gli ID per giorno
      const ids = [hashId(reminderId)];
      for (const day of [0, 1, 2, 3, 4, 5, 6]) {
        ids.push(hashId(`${reminderId}-${day}`));
      }
      await LocalNotifications.cancel({
        notifications: ids.map((id) => ({ id })),
      });
    } catch (e) {
      console.warn('[notifications] cancel error', e);
    }
  }
}

/** Cancella tutte le notifiche. */
export async function cancelAllReminders(): Promise<void> {
  clearAllWebTimeouts();
  if (Capacitor.isNativePlatform()) {
    try {
      const pending = await LocalNotifications.getPending();
      if (pending.notifications.length > 0) {
        await LocalNotifications.cancel({
          notifications: pending.notifications.map((n) => ({ id: n.id })),
        });
      }
    } catch (e) {
      console.warn('[notifications] cancel all error', e);
    }
  }
}

// ------------------------------------------------------------
// Web fallback (browser): setTimeout + Notification API
// ------------------------------------------------------------
const _webTimers = new Map<string, { timer: ReturnType<typeof setTimeout>; reschedule?: () => void }>();

function nextOccurrence(time: string, daysOfWeek: number[]): Date | null {
  const [h, m] = time.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    d.setHours(h, m, 0, 0);
    if (d.getTime() <= now.getTime()) continue;
    if (daysOfWeek.length === 0 || daysOfWeek.includes(d.getDay())) {
      return d;
    }
  }
  return null;
}

function scheduleWebTimeout(reminderId: string, delay: number, title: string, body: string, reschedule?: () => void) {
  cancelWebTimeout(reminderId);
  if (delay < 0) delay = 0;
  if (delay > 2 ** 31 - 1) delay = 2 ** 31 - 1;
  const timer = setTimeout(() => {
    fireWebNotification(title, body);
    if (reschedule) reschedule();
    else _webTimers.delete(reminderId);
  }, delay);
  _webTimers.set(reminderId, { timer, reschedule });
}

function cancelWebTimeout(reminderId: string) {
  const t = _webTimers.get(reminderId);
  if (t) {
    clearTimeout(t.timer);
    _webTimers.delete(reminderId);
  }
}

function clearAllWebTimeouts() {
  for (const { timer } of _webTimers.values()) clearTimeout(timer);
  _webTimers.clear();
}

function fireWebNotification(title: string, body: string) {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  try {
    const n = new Notification(title, {
      body,
      icon: '/icon.svg',
      badge: '/icon.svg',
      tag: 'pira-notes-reminder',
      requireInteraction: false,
    });
    setTimeout(() => n.close(), 8000);
  } catch (e) {
    console.warn('[notifications] web fire error', e);
  }
}
